import React, { useEffect, useRef, useState } from "react";
import Select from "react-select";
import styles from "./EmployeeComponents.module.css";
import { positionSelectStyles } from "../../../common/delSelect";
import { useAppStore } from "../../../../store/appStore";
import { EmployeeShiftsModal } from "./EmployeeShiftsModal";

// Compute touch-aware React Select props to avoid iOS virtual keyboard detaching menus
const useTouchAwareSelectProps = () => {
  const isTouch = useAppStore((s) => s.landingPage?.isTouch);
  const portalTarget =
    typeof document !== "undefined" ? document.body : undefined;
  if (isTouch) {
    return {
      menuPosition: "absolute",
      menuPlacement: "auto",
      menuShouldScrollIntoView: false,
      menuPortalTarget: portalTarget,
      styles: {
        menu: (base) => ({ ...base, zIndex: 9999 }),
        menuPortal: (base) => ({ ...base, zIndex: 9999 }),
      },
    };
  }
  return {
    menuPosition: "fixed",
    menuPortalTarget: portalTarget,
    styles: {
      menu: (base) => ({ ...base, zIndex: 9999 }),
      menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    },
  };
};

// Employee shift row component - read-only display for the card
const EmployeeShiftRow = ({ shift }) => {
  return (
    <tr>
      <td>{shift.startTime || "-"}</td>
      <td>{shift.endTime || "-"}</td>
      <td>{shift.totalTime != null ? shift.totalTime : "-"}</td>
    </tr>
  );
};

// Helper function to calculate total hours across all shifts
const calculateTotalEmployeeHours = (shifts) => {
  return shifts
    .reduce((sum, shift) => {
      return sum + (shift.totalTime || 0);
    }, 0)
    .toFixed(2);
};

// The employee card component - displays employee info and shifts in a card layout
export const EmployeeCard = React.forwardRef(
  ({ employee, callbacks, removeEmployeeRef }, ref) => {
    const touchSelectProps = useTouchAwareSelectProps();
    const showNotification = useAppStore(
      (s) => s.notificationActions.showNotification,
    );

    // State for shifts modal
    const [isShiftsModalOpen, setIsShiftsModalOpen] = useState(false);

    // Find any errors for this specific employee row
    const employeeErrors =
      callbacks?.delData?.errors?.employees?.find(
        (error) => error.employeeRowId === employee.employeeRowId,
      )?.errors || {};

    // Get autosave row errors for this employee
    const autosaveRowError = callbacks.getAutosaveRowError?.(
      employee.employeeRowId,
    );
    const autosaveFieldErrors = autosaveRowError?.field_errors || {};

    // Helper function to get placeholder text with error styling if needed
    const getPlaceholderWithError = (defaultText, errorKey) => {
      if (employeeErrors[errorKey]) {
        return (
          <span className={styles.errorText}>{employeeErrors[errorKey]}</span>
        );
      }
      return defaultText;
    };

    // Helper function to get autosave field error
    const getAutosaveFieldError = (fieldName) => {
      return autosaveFieldErrors[fieldName] || null;
    };

    // Handling the remove employee button
    const handleRemoveEmployee = () => {
      removeEmployeeRef.current = true;
      callbacks.removeEmployee(employee.employeeRowId, callbacks.emitPatch);
    };

    // Handling the employee selection
    const handleSelectEmployee = (selected) => {
      // Check for duplicate employee selection (skip check when clearing)
      if (selected?.value) {
        const duplicate = callbacks.delData.employees.find(
          (e) =>
            e.employeeId === selected.value &&
            e.employeeRowId !== employee.employeeRowId,
        );
        if (duplicate) {
          showNotification({
            type: "error",
            title: "Duplicate Employee",
            message: `"${selected.label}" is already assigned to another employee card on this DEL.`,
          });
          return;
        }
      }
      removeEmployeeRef.current = true;
      callbacks.selectEmployee(
        employee.employeeRowId,
        selected?.value || null,
        selected?.label || null,
        callbacks.emitPatch,
      );
    };

    // Handling the employee position selection
    const handleSelectEmployeePosition = (selected) => {
      removeEmployeeRef.current = true;
      callbacks.selectEmployeePosition(
        employee.employeeRowId,
        selected?.value || null,
        selected?.label || null,
        callbacks.emitPatch,
      );
    };

    // Handle saving shifts from modal - determines which patches to emit
    const handleSaveShifts = (tempState) => {
      const { shifts, existingShiftIds, originalShifts } = tempState;
      const currentShiftIds = Object.keys(shifts);

      // 1. Determine REMOVES (existed before, not in current)
      const removedIds = existingShiftIds.filter(
        (id) => !currentShiftIds.includes(id),
      );

      // 2. Determine ADDS (in current, not in existingShiftIds)
      const addedIds = currentShiftIds.filter(
        (id) => !existingShiftIds.includes(id),
      );

      // 3. Determine UPDATES (in both, but values changed)
      const updatedIds = currentShiftIds.filter((id) => {
        if (!existingShiftIds.includes(id)) return false;
        const original = originalShifts[id];
        const current = shifts[id];
        return (
          original.startTime !== current.startTime ||
          original.endTime !== current.endTime
        );
      });

      // 4. Emit patches for removes
      removedIds.forEach((shiftId) => {
        callbacks.removeEmployeeShiftRow(
          employee.employeeRowId,
          shiftId,
          callbacks.emitPatch,
        );
      });

      // 5. Emit patches for adds
      addedIds.forEach((shiftId) => {
        const shiftData = shifts[shiftId];
        callbacks.addNewEmployeeShiftRowWithId(
          employee.employeeRowId,
          shiftId,
          shiftData.startTime,
          shiftData.endTime,
          callbacks.emitPatch,
        );
      });

      // 6. Emit patches for updates
      updatedIds.forEach((shiftId) => {
        const shiftData = shifts[shiftId];
        const original = originalShifts[shiftId];

        // Update start time if changed
        if (original.startTime !== shiftData.startTime) {
          callbacks.updateEmployeeShiftStartTime(
            employee.employeeRowId,
            shiftId,
            shiftData.startTime,
            callbacks.emitPatch,
          );
        }
        // Update end time if changed
        if (original.endTime !== shiftData.endTime) {
          callbacks.updateEmployeeShiftEndTime(
            employee.employeeRowId,
            shiftId,
            shiftData.endTime,
            callbacks.emitPatch,
          );
        }
      });

      // Close modal
      setIsShiftsModalOpen(false);
    };

    // Calculate total hours for display
    const totalHours = calculateTotalEmployeeHours(employee.shifts);
    const shiftCount = employee.shifts.length;
    const hasShiftErrors =
      employeeErrors?.employeeShiftError || employeeErrors?.shifts?.length > 0;

    return (
      <div className={styles.employeeCard} data-employee-row="true" ref={ref}>
        {/* Employee Name */}
        <div className={styles.employeeCardHeader}>
          <label className={styles.fieldLabel}>Name</label>
          <Select
            value={
              employee.employeeName
                ? {
                    value: employee.employeeId,
                    label: employee.employeeName,
                  }
                : null
            }
            onChange={(selected) => {
              handleSelectEmployee(selected);
            }}
            options={callbacks.allEmployees.map((emp) => ({
              value: emp.employee_id,
              label: emp.label,
            }))}
            isDisabled={callbacks.delMode === "view"}
            styles={{
              ...positionSelectStyles(
                !!employeeErrors?.employeeId ||
                  !!getAutosaveFieldError("employee_id"),
              ),
              ...(touchSelectProps.styles || {}),
            }}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuPosition={touchSelectProps.menuPosition}
            menuPlacement={touchSelectProps.menuPlacement}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
            placeholder={getPlaceholderWithError(
              "Select Employee",
              "employeeId",
            )}
            isClearable={true}
            isSearchable={true}
            className={
              employeeErrors?.employeeId || getAutosaveFieldError("employee_id")
                ? styles.errorSelectContainer
                : ""
            }
          />
          {getAutosaveFieldError("employee_id") && (
            <span className={styles.fieldErrorText}>
              {getAutosaveFieldError("employee_id")}
            </span>
          )}
        </div>

        {/* Employee Position */}
        <div className={styles.employeeCardHeader}>
          <label className={styles.fieldLabel}>Position</label>
          <Select
            value={
              employee?.positionName
                ? {
                    value: employee.positionId,
                    label: employee.positionName,
                  }
                : null
            }
            onChange={(selected) => {
              handleSelectEmployeePosition(selected);
            }}
            options={callbacks.allEmployeePositions.map((position) => ({
              value: position.cat_id,
              label: position.label,
            }))}
            isDisabled={callbacks.delMode === "view"}
            styles={{
              ...positionSelectStyles(
                !!employeeErrors?.employeePosition ||
                  !!getAutosaveFieldError("position_id"),
              ),
              ...(touchSelectProps.styles || {}),
            }}
            menuPortalTarget={touchSelectProps.menuPortalTarget}
            menuPosition={touchSelectProps.menuPosition}
            menuPlacement={touchSelectProps.menuPlacement}
            menuShouldScrollIntoView={touchSelectProps.menuShouldScrollIntoView}
            placeholder={getPlaceholderWithError(
              "Select Position",
              "employeePosition",
            )}
            isClearable={true}
            isSearchable={true}
            className={
              employeeErrors?.employeePosition ||
              getAutosaveFieldError("position_id")
                ? styles.errorSelectContainer
                : ""
            }
          />
          {getAutosaveFieldError("position_id") && (
            <span className={styles.fieldErrorText}>
              {getAutosaveFieldError("position_id")}
            </span>
          )}
        </div>

        {/* Per Diem Toggle */}
        <div className={styles.perDiemContainer}>
          <label className={styles.perDiemLabel}>
            <span className={styles.perDiemText}>Per Diem</span>
            <input
              type="checkbox"
              checked={employee.perDiem || false}
              onChange={(e) => {
                callbacks.updateEmployeePerDiem(
                  employee.employeeRowId,
                  e.target.checked,
                  callbacks.emitPatch,
                );
              }}
              disabled={callbacks.delMode === "view" || !employee.employeeId}
              className={styles.perDiemCheckbox}
            />
          </label>
        </div>

        {/* Shifts Table + Edit Button */}
        <div
          className={
            hasShiftErrors
              ? styles.errorShiftsContainer
              : styles.shiftsContainer
          }
        >
          <table className={styles.shiftsTable}>
            <thead>
              <tr>
                <th>Start</th>
                <th>End</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              {employee.shifts.length > 0 ? (
                employee.shifts.map((shift) => (
                  <EmployeeShiftRow key={shift.id} shift={shift} />
                ))
              ) : (
                <tr>
                  <td
                    colSpan="3"
                    style={{ textAlign: "center", color: "#666" }}
                  >
                    No shifts added
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          <div className={styles.shiftsFooter}>
            <span className={styles.totalHoursLabel}>
              Total: {totalHours} hrs
            </span>
          </div>
          <button
            onClick={() => setIsShiftsModalOpen(true)}
            className={styles.manageShiftsButton}
            disabled={!employee.employeeId}
          >
            {callbacks.delMode === "view" ? "View Shifts" : "Edit Shifts"}
          </button>
        </div>

        {/* Card Actions */}
        <div className={styles.employeeCardActions}>
          <button
            onClick={handleRemoveEmployee}
            className={styles.removeEmployeeButton}
            disabled={callbacks.delMode === "view"}
          >
            Remove Employee
          </button>
        </div>

        {/* Shifts Modal */}
        <EmployeeShiftsModal
          isOpen={isShiftsModalOpen}
          onClose={() => setIsShiftsModalOpen(false)}
          onSave={handleSaveShifts}
          employee={employee}
          delMode={callbacks.delMode}
          errors={employeeErrors}
        />
      </div>
    );
  },
);

// Helper to get autosave row error for a specific employee row
const getAutosaveRowError = (autosaveErrors, rowId) => {
  if (!autosaveErrors?.rowErrors) return null;
  return autosaveErrors.rowErrors[rowId] || null;
};

// This is the main component, holding the information for the employee Tab with card-based layout.
export const EmployeeTab = ({ employeeProps, actions }) => {
  // Card grid ref
  const cardGridRef = useRef(null);
  // Putting this here to watch for changes in the remove employee button, so we don't scroll when it's clicked.
  const removeEmployeeRef = useRef(null);
  // Adding this to keep the grid from scrolling when we pop in and out of the employee tab.
  const initialRender = useRef(true);
  // Ref to track the last employee card for scrolling
  const lastCardRef = useRef(null);

  // Setting an employee state here to make it easier to translate changes and keep compatibility.
  const employeeState = {
    // Core data
    delData: employeeProps.delData,
    delMode: employeeProps.delMode,
    delErrors: employeeProps.delErrors,
    allEmployees: employeeProps.allEmployees,
    allEmployeePositions: employeeProps.allEmployeePositions,

    // UI state
    loading: employeeProps.employeeUI.loading,
    showAll: employeeProps.showAll,

    // Action methods - delegate to global actions
    ...actions,

    // DEL autosave websockets - passed down from DelForm
    emitPatch: employeeProps.emitPatch,

    // Autosave row errors for inline error display
    autosaveErrors: employeeProps.autosaveErrors,
    getAutosaveRowError: (rowId) =>
      getAutosaveRowError(employeeProps.autosaveErrors, rowId),
  };

  // Watching employee rows for changes, and scrolling to the last card if the number of cards changes.
  useEffect(() => {
    if (employeeState.showAll) return;
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }
    // Only auto-scroll on add (not remove)
    if (!removeEmployeeRef.current && lastCardRef.current) {
      // Use setTimeout to ensure the DOM has updated and the card is rendered
      setTimeout(() => {
        lastCardRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }, 100);
    }
    // reset the flag so subsequent non-add operations don't prevent scrolls
    removeEmployeeRef.current = null;
  }, [employeeState.delData.employees.length]);

  useEffect(() => {
    return () => {
      initialRender.current = true;
    };
  }, [employeeState.setEmployeeRows]);

  // Handling the add employee button
  const handleAddEmployee = () => {
    removeEmployeeRef.current = false;
    employeeState.addNewEmployee();
  };

  return (
    <div className={styles.employeeTabContainer}>
      <div className={styles.employeeCardGrid} ref={cardGridRef}>
        {/* Adding a new employee card for each employee in the delData.employees array */}
        {employeeState.delData.employees.map((employee, index) => (
          <EmployeeCard
            employee={employee}
            callbacks={employeeState}
            key={employee.employeeRowId}
            removeEmployeeRef={removeEmployeeRef}
            ref={
              index === employeeState.delData.employees.length - 1
                ? lastCardRef
                : null
            }
          />
        ))}
      </div>
      <div className={styles.addEmployeeButtonContainer}>
        {employeeProps.delMode !== "view" && (
          <button
            onClick={handleAddEmployee}
            className={styles.addEmployeeButton}
          >
            Add Employee
          </button>
        )}
      </div>
    </div>
  );
};
